import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { STOCK_STATUS_LABELS, STOCK_STATUSES, formatCents, formatQuantityMilli, type InventoryOverview, type Paginated, type ProductOption, type StockItem } from '@kontor/shared';
import { api, queryString } from '../../api';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import { SortTh } from '../../components/SortTh';
import { useSession } from '../../session';
import { ProductPicker } from '../offers/ProductPicker';
import { BookingPanel, BOOKING_LABELS, type BookingMode } from './BookingPanel';
import { InventoryTabs, errorText, formatDay, statusClass, useLocations } from './shared';
import './inventory.css';

/**
 * Lager (KON-166): Übersicht mit Warnungen, darunter die Bestände aller Artikel mit Lagerführung. Von hier aus bucht man Wareneingang, Warenausgang, Umlagerung und
 * Korrektur. Artikel nimmt man über „Artikel aufnehmen“ ins Lager, die Lagerführung gilt je Produkt.
 */
export function StockPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const status = params.get('status') ?? '';
  const locationId = params.get('locationId') ?? '';
  const sort = params.get('sort') ?? '';
  const page = Number(params.get('page') ?? 1);
  const [booking, setBooking] = useState<BookingMode | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const locations = useLocations();
  const mayWrite = can('inventory.write');

  const overview = useQuery({ queryKey: ['inventory-overview'], queryFn: async () => (await api<{ data: InventoryOverview }>('GET', '/inventory/overview')).data });
  const list = useQuery({
    queryKey: ['inventory-stock', { q, status, locationId, sort, page }],
    queryFn: () => api<Paginated<StockItem>>('GET', `/inventory/stock${queryString({ q, status, locationId, sort, page, perPage: 25 })}`),
    placeholderData: keepPreviousData,
  });
  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    if (!('page' in next)) merged.delete('page');
    setParams(merged);
  };
  const fields: FilterField[] = [
    { key: 'status', label: 'Bestand', type: 'select', options: [{ value: '', label: 'Alle' }, ...STOCK_STATUSES.map((s) => ({ value: s, label: STOCK_STATUS_LABELS[s] }))] },
    { key: 'locationId', label: 'Lagerort', type: 'select', options: [{ value: '', label: 'Alle' }, ...(locations.data ?? []).map((l) => ({ value: l.id, label: l.path }))] },
  ];
  const track = useMutation({
    mutationFn: (p: ProductOption) => api('PUT', `/inventory/stock/${p.id}/settings`, { tracked: true, minStockMilli: 0, batchTracking: false }),
    onSuccess: (_d, p) => {
      for (const key of ['inventory-stock', 'inventory-stock-options', 'inventory-overview']) client.invalidateQueries({ queryKey: [key] });
      setMessage({ ok: true, text: `„${p.name}“ wird jetzt im Lager geführt. Mindestbestand und Chargenführung stellen Sie in der Artikelansicht ein.` });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Der Artikel konnte nicht aufgenommen werden.') }),
  });
  const data = overview.data;

  return (
    <>
      <div className="page-head">
        <h1>Lager</h1>
        <span className="muted">{list.data ? `${list.data.meta.total} Artikel` : ''}</span>
      </div>
      <InventoryTabs />
      {data && (
        <div className="inv-tiles">
          <div className="card inv-tile"><span className="muted small">Artikel im Lager</span><strong>{data.trackedProducts}</strong></div>
          <div className="card inv-tile"><span className="muted small">Unter Mindestbestand</span><strong className={data.lowStock.length ? 'inv-warn' : ''}>{data.lowStock.length}</strong></div>
          <div className="card inv-tile"><span className="muted small">Bald ablaufende Chargen</span><strong className={data.expiringBatches.length ? 'inv-warn' : ''}>{data.expiringBatches.length}</strong></div>
          <div className="card inv-tile"><span className="muted small">Offene Inventuren</span><strong>{data.openCounts}</strong></div>
          {data.stockValueCents !== null && <div className="card inv-tile"><span className="muted small">Bestandswert (Einkauf)</span><strong>{formatCents(data.stockValueCents)}</strong></div>}
          {data.potentialRevenueCents !== null && <div className="card inv-tile"><span className="muted small">Möglicher Umsatz netto</span><strong>{formatCents(data.potentialRevenueCents)}</strong><span className="muted small">Bestand zum Verkaufspreis</span></div>}
          <Link to="/inventar" className="card inv-tile inv-tile--link"><span className="muted small">Inventar (Betriebsmittel)</span><strong>{data.assets.total}</strong>{data.assets.warrantyExpiring > 0 && <span className="inv-warn small">{data.assets.warrantyExpiring} Garantie läuft ab</span>}</Link>
        </div>
      )}
      {data && data.expiringBatches.length > 0 && (
        <section className="card" aria-labelledby="inv-exp">
          <h2 id="inv-exp">Chargen, die ablaufen</h2>
          <ul className="inv-list">
            {data.expiringBatches.map((b) => (
              <li key={b.batchId}>
                <Link to={`/lager/bestand/${b.productId}`}>{b.productName}</Link> · Charge {b.batchNumber} · {formatQuantityMilli(b.quantityMilli)} {b.unitSymbol ?? ''} · <span className={b.expired ? 'inv-bad' : 'inv-warn'}>{b.expired ? 'abgelaufen' : 'läuft ab'} am {formatDay(b.bestBefore)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      {mayWrite && (
        <div className="inv-actions">
          {(['receipt', 'issue', 'transfer', 'adjustment'] as BookingMode[]).map((m) => (
            <button key={m} type="button" className={`btn ${booking === m ? 'btn--primary' : 'btn--secondary'}`} onClick={() => setBooking(booking === m ? null : m)}>{BOOKING_LABELS[m]}</button>
          ))}
        </div>
      )}
      {booking && <BookingPanel key={booking} mode={booking} onClose={() => setBooking(null)} onDone={(moved) => { setBooking(null); setMessage({ ok: true, text: `Gebucht (${moved.length} ${moved.length === 1 ? 'Bewegung' : 'Bewegungen'}).` }); }} />}
      {mayWrite && (
        <div className="inv-track">
          <span className="muted small">Artikel ins Lager aufnehmen: Waren aus Ihren Produkten, auch Verbrauchsmaterial ohne Verkauf</span>
          <ProductPicker purpose="stock" onPick={(p) => track.mutate(p)} />
        </div>
      )}
      <FilterBar search={{ value: q, placeholder: 'Name, Artikelnummer oder EAN', onSubmit: (v) => update({ q: v }) }} fields={fields} values={{ status, locationId }} onChange={(k, v) => update({ [k]: v })} onReset={() => setParams(new URLSearchParams())} />
      {list.isError && <div className="alert alert--error">{(list.error as Error).message}</div>}
      <div className="card table-card">
        <table className="table table--cards">
          <thead>
            <tr>
              <SortTh label="Artikel" sortKey="name" sort={sort} onSort={(s) => update({ sort: s })} />
              <SortTh label="Nr." sortKey="sku" sort={sort} onSort={(s) => update({ sort: s })} />
              <SortTh label="Bestand" sortKey="quantity" sort={sort} onSort={(s) => update({ sort: s })} first="desc" className="num" />
              <th className="num">Mindestbestand</th>
              <th>Status</th>
              {data?.stockValueCents !== null && data !== undefined && <SortTh label="Wert (Einkauf)" sortKey="value" sort={sort} onSort={(s) => update({ sort: s })} first="desc" className="num" />}
              {data?.stockValueCents !== null && data !== undefined && <SortTh label="Möglicher Umsatz netto" sortKey="revenue" sort={sort} onSort={(s) => update({ sort: s })} first="desc" className="num" />}
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={7} className="muted">Wird geladen …</td></tr>}
            {list.data?.data.length === 0 && <tr><td colSpan={6} className="empty">{q || status || locationId ? 'Keine Artikel zu dieser Suche.' : 'Noch kein Artikel im Lager. Nehmen Sie oben einen Artikel auf.'}</td></tr>}
            {list.data?.data.map((s) => (
              <ExpandRow
                key={s.productId}
                summary={<Summary title={<Link to={`/lager/bestand/${s.productId}`} className="strong-link">{s.name}</Link>} aside={<span className={`inv-badge ${statusClass[s.status]}`}>{STOCK_STATUS_LABELS[s.status]}</span>} line2={`${formatQuantityMilli(s.quantityMilli)} ${s.unitSymbol ?? ''}`} line3={s.sku ?? undefined} />}
              >
                <td className="cell-desktop-title"><Link to={`/lager/bestand/${s.productId}`} className="strong-link">{s.name}</Link>{s.batchTracking && <div className="muted small">in Chargen</div>}</td>
                <td data-label="Nr.">{s.sku ?? '–'}</td>
                <td data-label="Bestand" className="num">{formatQuantityMilli(s.quantityMilli)} {s.unitSymbol ?? ''}</td>
                <td data-label="Mindestbestand" className="num">{s.minStockMilli ? `${formatQuantityMilli(s.minStockMilli)} ${s.unitSymbol ?? ''}` : '–'}</td>
                <td data-label="Status"><span className={`inv-badge ${statusClass[s.status]}`}>{STOCK_STATUS_LABELS[s.status]}</span></td>
                {data?.stockValueCents !== null && data !== undefined && <td data-label="Wert (Einkauf)" className="num">{s.valueCents !== null ? formatCents(s.valueCents) : '–'}</td>}
                {data?.stockValueCents !== null && data !== undefined && (
                  <td data-label="Möglicher Umsatz netto" className="num">
                    {s.potentialRevenueCents !== null ? formatCents(s.potentialRevenueCents) : <span className="muted">nicht verkauft</span>}
                    {s.salePriceCents !== null && s.quantityMilli > 0 && <div className="muted small">{formatQuantityMilli(s.quantityMilli)} × {formatCents(s.salePriceCents)}</div>}
                  </td>
                )}
              </ExpandRow>
            ))}
          </tbody>
        </table>
      </div>
      {list.data && list.data.meta.lastPage > 1 && (
        <div className="pager">
          <button type="button" className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>Zurück</button>
          <span className="muted">Seite {page} von {list.data.meta.lastPage}</span>
          <button type="button" className="btn btn--secondary btn--sm" disabled={page >= list.data.meta.lastPage} onClick={() => update({ page: String(page + 1) })}>Weiter</button>
        </div>
      )}
    </>
  );
}
